/**
 * @name TotemHomePage
 * @author Keven Adan Bezerra | kevenadanb
 * @description Página principal do totem com layout moderno de 3 colunas
 * @returns {JSX.Element} - Página principal do totem
 * @package src/components/pages/TotemHomePage.tsx
 */

import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import GradientBackground from "@/components/common/backgrounds/GradientBackground";
import OperadorControl from "@/components/common/OperadorControl";
import { useCarrinho } from "@/contexts/CarrinhoContext";
import { useLoadingContext } from "@/contexts/LoadingContext";
import useThemeContext from "@/themes/hooks/useThemeContext";
import {
  getGlassAppBar,
  getGlassButton,
  getGlassEffect,
} from "@/themes/styles/glassStyles";
// import { useOperadorContext } from '@/contexts/OperadorContext'; // TODO: Implementar lógica de totem liberado
import { usePedido } from "@/contexts/PedidoContext";
import { useUserContext } from "@/contexts/UserContext";
import { IdentificacaoAluno } from ".";
import CarrinhoView from "./CarrinhoView";
import TotemInterface from "./TotemInterface";

type TelaAtiva = "identificacao" | "produtos" | "carrinho" | "pedido";

const TotemHomePage: React.FC = () => {
  const { theme } = useThemeContext();
  const { alunoAtual, limparIdentificacaoAluno } = useUserContext();
  const { limparCarrinho } = useCarrinho();
  const { pedidoAtual } = usePedido();
  const { loadingStates } = useLoadingContext();
  // const { isTotemLiberado } = useOperadorContext(); // TODO: Implementar lógica de totem liberado

  const [telaAtiva, setTelaAtiva] = useState<TelaAtiva>("identificacao");

  // Navegação baseada no estado do aluno
  useEffect(() => {
    if (loadingStates["global-loading"]) return;

    if (alunoAtual && telaAtiva === "identificacao") {
      console.log("✅ Navegando para produtos");
      setTelaAtiva("produtos");
      return;
    }

    if (!alunoAtual && telaAtiva !== "identificacao") {
      console.log("⚠️ Sem aluno, voltando para identificação");
      setTelaAtiva("identificacao");
      return;
    }
  }, [alunoAtual, loadingStates]); // Removido telaAtiva para evitar loops

  // Handlers de navegação otimizados
  const handleVoltarIdentificacao = useCallback(() => {
    limparIdentificacaoAluno();
    limparCarrinho();
    setTelaAtiva("identificacao");
  }, [limparIdentificacaoAluno, limparCarrinho]);

  const handleIrParaProdutos = useCallback(() => {
    setTelaAtiva("produtos");
  }, []); // Removido alunoAtual da dependência

  const handleIrParaCarrinho = useCallback(() => {
    setTelaAtiva("carrinho");
  }, []); // Removido alunoAtual da dependência

  const handleConfirmarPedido = useCallback(() => {
    setTelaAtiva("pedido");
  }, []); // Removido alunoAtual da dependência

  const handleFinalizarPedido = useCallback(() => {
    handleVoltarIdentificacao();
  }, [handleVoltarIdentificacao]);

  // Renderização das telas
  const renderContent = () => {
    switch (telaAtiva) {
      case "identificacao":
        return (
          <IdentificacaoAluno onAlunoIdentificado={handleIrParaProdutos} />
        );

      case "produtos":
        return alunoAtual ? (
          <TotemInterface onIrParaCarrinho={handleIrParaCarrinho} />
        ) : (
          <IdentificacaoAluno onAlunoIdentificado={handleIrParaProdutos} />
        );

      case "carrinho":
        return alunoAtual ? (
          <CarrinhoView
            onVoltarProdutos={handleIrParaProdutos}
            onConfirmarPedido={handleConfirmarPedido}
          />
        ) : (
          <IdentificacaoAluno onAlunoIdentificado={handleIrParaProdutos} />
        );

      case "pedido":
        return alunoAtual && pedidoAtual ? (
          <PedidoConfirmacao
            pedido={pedidoAtual}
            onNovoPedido={handleFinalizarPedido}
          />
        ) : (
          <IdentificacaoAluno onAlunoIdentificado={handleIrParaProdutos} />
        );

      default:
        return (
          <IdentificacaoAluno onAlunoIdentificado={handleIrParaProdutos} />
        );
    }
  };

  return (
    <GradientBackground>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Controle do Operador - sempre visível quando totem está liberado */}
        <OperadorControl showFullBar={true} />

        {/* AppBar - aparece apenas quando há aluno logado e não está na identificação */}
        {alunoAtual && telaAtiva !== "identificacao" && (
          <AppBar
            position="static"
            elevation={0}
            sx={{
              ...getGlassAppBar(theme),
              backgroundColor: "transparent",
            }}
          >
            <Toolbar>
              <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    ...getGlassEffect(theme, "light"),
                    bgcolor: "transparent",
                    color: theme.palette.primary.main,
                    fontWeight: "bold",
                  }}
                >
                  {alunoAtual.nome?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
                  >
                    {alunoAtual.nome}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Saldo: R$ {(alunoAtual.saldo || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="outlined"
                onClick={handleVoltarIdentificacao}
                sx={{
                  ...getGlassEffect(theme, "light"),
                  background: `linear-gradient(135deg, #ff1744aa, #ff174466)`,
                  color: "#ff1744",
                  border: `2px solid #ff1744`,
                  minWidth: 100,
                  fontWeight: "bold",
                  boxShadow: `0 4px 16px rgba(255, 23, 68, 0.4)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, #ff1744dd, #ff174499)`,
                    color: "#ffffff",
                    transform: "translateY(-2px)",
                    boxShadow: `
                                                       0 8px 24px rgba(255, 23, 68, 0.6),
                                                       inset 0 1px 0 rgba(255, 255, 255, 0.3)
                                                  `,
                  },
                  "&:active": {
                    transform: "translateY(0)",
                    background: `linear-gradient(135deg, #ff1744ff, #ff1744bb)`,
                    boxShadow: `
                                                       0 2px 12px rgba(255, 23, 68, 0.5),
                                                       inset 0 1px 0 rgba(255, 255, 255, 0.2)
                                                  `,
                  },
                }}
              >
                Cancelar
              </Button>
            </Toolbar>
          </AppBar>
        )}

        {/* Conteúdo Principal */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </GradientBackground>
  );
};

// Componente para tela de confirmação do pedido
interface PedidoConfirmacaoProps {
  pedido: any;
  onNovoPedido: () => void;
}

const PedidoConfirmacao: React.FC<PedidoConfirmacaoProps> = ({
  pedido,
  onNovoPedido,
}) => {
  const { theme } = useThemeContext();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: "center",
            p: 4,
            ...getGlassEffect(theme, "strong"),
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h3"
            color="primary"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 3 }}
          >
            🎉 Pedido Confirmado!
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Comanda #{pedido.numero_comanda}
          </Typography>

          <Typography variant="h5" sx={{ mb: 3 }}>
            Valor: R$ {pedido.valor_total.toFixed(2)}
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, fontSize: "1.1rem" }}>
            Apresente esta comanda no balcão para retirar seu pedido.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={onNovoPedido}
            sx={{
              ...getGlassButton(theme, "primary"),
              fontSize: "1.3rem",
              py: 2,
              px: 6,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Fazer Novo Pedido
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default TotemHomePage;
